import {createContext, useContext, useEffect, useState} from 'react';
import {classNames} from 'utils';
import {Color, cssColorValues} from './colors';
import styles from './tabs.module.css';

// The tabs context allows any children of <Tabs> to change the selected tab.

type TabsContextProps = {
  setTab: (label: string) => void;
};

const TabsContext = createContext<TabsContextProps>({
  setTab: () => {},
});

export const useTabs = () => useContext(TabsContext);

export function Tabs({tabs, initialTab, color = 'orange', size = 'md', onChange}: Props) {
  const [selected, setSelected] = useState('');

  useEffect(() => {
    const labels = tabs.map(({label}) => label);
    if (!labels.includes(selected)) setSelected(initialTab || tabs[0].label);
  }, [tabs]);

  const handleTabChange = (label: string) => {
    const labels = tabs.map(({label}) => label);
    if (labels.includes(label)) setSelected(label);
  };

  useEffect(() => {
    if (initialTab) setSelected(initialTab);
  }, [initialTab]);

  return (
    <TabsContext.Provider
      value={{
        setTab: (label) => {
          handleTabChange(label);
          window.scrollTo({top: 0, behavior: 'smooth'});
        },
      }}>
      <div className={classNames([styles.tabs, styles[size]])}>
        {tabs.map((tab) => (
          <button
            key={tab.label}
            className={classNames([
              styles.tabButton,
              tab.label === selected && styles.selected,
              styles[size],
            ])}
            style={tab.label === selected ? {borderColor: cssColorValues[color]} : undefined}
            disabled={tab.disabled}
            onClick={() => {
              if (!tab.allowClick || tab.allowClick()) {
                setSelected(tab.label);
                if (onChange) onChange(tab.label);
                if (tab.onClick) tab.onClick();
              }
            }}>
            {tab.label}
          </button>
        ))}
      </div>
      {tabs.find(({label}) => label === selected)?.children || null}
    </TabsContext.Provider>
  );
}

export type Props = {
  tabs: Tab[];
  initialTab?: string;
  color?: Color;
  size?: 'sm' | 'md' | 'lg';
  onChange?: (tabLabel: string) => void;
};

export type Tab = {
  label: string;
  disabled?: boolean;
  allowClick?: () => boolean;
  onClick?: () => void;
  children?: JSX.Element;
};
